import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { DOMSerializer, DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { ResizableImage } from './extensions/ResizableImage';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import axios from 'axios';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core';

import './NotionEditor.css';
import { PageBreak } from './PageBreak';
import { GrammarCheck } from './extensions/GrammarCheck';
import { endpoints, baseUrl } from '../../api/api';
import { Section } from './types';
import { EditorToolbar, SectionSidebar, GrammarSuggestionPopup, EditorTour, TOUR_KEY, TipsPanel } from './components';
import { useImageUpload, useBookLoader, useGrammarCheck } from './hooks';
import { DEFAULT_FRONT_MATTER } from './hooks/useBookLoader';
import { useAlert } from '../../components/AlertBanner';
import { trackEditorOpened, trackEditorReady, trackNewPage, trackDeletePage, trackAutosave, trackAutosaveFailed, trackEditorAbandoned, trackSaveAndContinueFailed } from '../../utils/analytics';

// Build the save payload from sections (shared by save handlers and sendBeacon)
const buildSavePayload = (secs: Section[]) => {
  const combinedHtml = secs.map(s =>
    `<div class="section" data-section-id="${s.id}" data-section-type="${s.type}"><h2>${s.title}</h2>${s.content}</div>`
  ).join('');
  return { content: { html: combinedHtml, json: { sections: secs }, lastSavedAt: new Date().toISOString() } };
};

const NotionEditor: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const bookId = searchParams.get('bookId');
  const [isSaving, setIsSaving] = useState(false);
  const [showTour, setShowTour] = useState(!localStorage.getItem(TOUR_KEY));
  const hasUnsavedChanges = useRef(false);
  const sectionsRef = useRef<Section[]>([]);
  const activeSectionIdRef = useRef<string>('dedication');
  const isLoadingRef = useRef(false);
  // Default no-op: returns HTML unchanged until useImageUpload wires in the real handler
  const pasteHandlerRef = useRef<(html: string) => Promise<string>>(async (html) => html);

  const editor = useEditor({
    // This is a pure client-side SPA (no SSR) — Tiptap v3's default of deferring
    // the view's mount to a later render (to avoid SSR hydration mismatches) isn't
    // solving a problem this app has, and it races with effects elsewhere in this
    // component that access editor.view synchronously on mount.
    immediatelyRender: true,
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
      PageBreak,
      GrammarCheck.configure({ debounceMs: 1200 }),
      ResizableImage,
      Table.configure({ resizable: true, HTMLAttributes: { class: 'tiptap-table' } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: '',
    editorProps: {
      handlePaste: (view, event) => {
        const html = event.clipboardData?.getData('text/html') ?? '';
        if (!html.includes('data:image')) return false;

        // Intercept: compress + upload base64 images before they enter the doc.
        // We prevent the default paste and re-insert the processed HTML once done.
        event.preventDefault();
        pasteHandlerRef.current(html)
          .then((processedHtml) => {
            const parser = ProseMirrorDOMParser.fromSchema(view.state.schema);
            const dom = document.createElement('div');
            dom.innerHTML = processedHtml;
            const slice = parser.parseSlice(dom, { preserveWhitespace: 'full' });
            view.dispatch(view.state.tr.replaceSelection(slice));
          })
          .catch((err) => console.error('Image paste processing failed:', err));
        return true;
      },
    },
    onUpdate: ({ editor }) => {
      // Skip section updates while book is loading to prevent overwriting loaded content
      if (isLoadingRef.current) return;
      // Use refs to avoid stale closure over sections/activeSectionId
      const currentSections = sectionsRef.current;
      const currentActiveId = activeSectionIdRef.current;
      const activeSection = currentSections.find(s => s.id === currentActiveId);
      if (activeSection) {
        const updatedSections = currentSections.map(s =>
          s.id === currentActiveId ? { ...s, content: editor.getHTML() } : s
        );
        setSections(updatedSections);
      }
      hasUnsavedChanges.current = true;
    },
  });

  // Warn on browser refresh/tab close and attempt best-effort save
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges.current || isLoadingRef.current || loadFailedRef.current) return;
      e.preventDefault();
      // Best-effort save via fetch with keepalive (sendBeacon only supports POST, but route is PUT)
      if (bookId) {
        const payload = buildSavePayload(sectionsRef.current);
        const url = `${baseUrl}${endpoints.updateBook.replace(':id', bookId)}`;
        const token = localStorage.getItem('accessToken');
        fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
          keepalive: true,
        });
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [bookId]);


  // Track editor opened on mount, abandoned on unmount
  useEffect(() => {
    trackEditorOpened();
    return () => { trackEditorAbandoned(); };
  }, []);

  // Track editor ready when editor instance is available
  useEffect(() => {
    if (editor) { trackEditorReady(); }
  }, [editor]);

  const {
    sections, setSections, activeSectionId, setActiveSectionId,
    isLoading, loadFailed, bookTitle, bookLanguage
  } = useBookLoader(bookId);

  const loadFailedRef = useRef(false);

  // Keep refs in sync for use in onUpdate, beforeunload, and autosave
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    activeSectionIdRef.current = activeSectionId;
  }, [activeSectionId]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    loadFailedRef.current = loadFailed;
  }, [loadFailed]);

  // After a successful load, clear the stale "unsaved" flag set by the initial onUpdate firing
  useEffect(() => {
    if (!isLoading && !loadFailed) {
      hasUnsavedChanges.current = false;
    }
  }, [isLoading, loadFailed]);

  const { fileInputRef, handleImageUpload, handleFileChange, processAndUploadImages, handlePasteImages } = useImageUpload(bookId, editor);

  // Stable refs so setInterval / handlePaste closures always call the latest versions
  // without needing them in dependency arrays.
  const processAndUploadImagesRef = useRef(processAndUploadImages);
  useEffect(() => { processAndUploadImagesRef.current = processAndUploadImages; }, [processAndUploadImages]);

  useEffect(() => { pasteHandlerRef.current = handlePasteImages; }, [handlePasteImages]);

  // Autosave every 2 minutes when there are unsaved changes (skip while loading or load failed)
  useEffect(() => {
    if (!bookId) return;
    const interval = setInterval(async () => {
      if (!hasUnsavedChanges.current || isLoadingRef.current || loadFailedRef.current) return;
      try {
        // Pre-clean: convert base64 images → GridFS URLs to keep the request body
        // small and avoid hitting the 50MB body-parser limit before server-side
        // extraction runs. We intentionally do NOT update React state or the editor
        // here — that only happens on manual save, where the user controls timing.
        // The server-side extractor is the guaranteed cleanup for any that slip through.
        const processedSections = await processAndUploadImagesRef.current(sectionsRef.current);

        // Update only the ref so subsequent autosaves use the cleaner sections
        // without triggering a re-render or overwriting the live editor state.
        sectionsRef.current = processedSections;

        const payload = buildSavePayload(processedSections);
        await axios.put(endpoints.updateBook.replace(':id', bookId), payload);
        hasUnsavedChanges.current = false;
        trackAutosave(new Date().toISOString());
      } catch (error: any) {
        trackAutosaveFailed(error.response?.status?.toString() || error.message || 'unknown');
      }
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [bookId]);

  const {
    errorCount, isChecking, triggerCheck, errors,
    activeError, setActiveError, dismissError, applySuggestion
  } = useGrammarCheck({ editor, language: bookLanguage, activeSectionId });

  // Handle clicks on grammar error decorations
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view?.dom;
    if (!dom) return;
    const clickHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      const errorEl = target.classList.contains('grammar-error')
        ? target
        : target.closest('.grammar-error') as HTMLElement | null;
      if (errorEl) {
        const errorId = errorEl.getAttribute('data-grammar-error-id');
        if (errorId) {
          const error = errors.find(e => e.id === errorId);
          if (error) { setActiveError(error); return; }
        }
      }
      if (!target.closest('.grammar-suggestion-popup')) {
        setActiveError(null);
      }
    };
    dom.addEventListener('click', clickHandler);
    return () => dom.removeEventListener('click', clickHandler);
  }, [editor, errors, setActiveError]);

  // Update editor content when active section changes or when loading completes
  useEffect(() => {
    if (editor && !isLoading) {
      const activeSection = sections.find(s => s.id === activeSectionId);
      if (activeSection) {
        editor.commands.setContent(activeSection.content || '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId, editor, isLoading]);

  const handleSaveContent = async () => {
    if (!bookId) { showAlert('No book selected. Please start from the book creation flow.', 'warning'); return; }

    setIsSaving(true);
    try {
      const processedSections = await processAndUploadImages(sections);
      setSections(processedSections);

      const activeProcessedSection = processedSections.find(s => s.id === activeSectionId);
      if (editor && activeProcessedSection) {
        editor.commands.setContent(activeProcessedSection.content);
      }

      await axios.put(endpoints.updateBook.replace(':id', bookId), buildSavePayload(processedSections));
      hasUnsavedChanges.current = false;
      trackAutosave(new Date().toISOString());
      showAlert('Content saved successfully!', 'success');
    } catch (error: any) {
      trackAutosaveFailed(error.response?.status?.toString() || error.message || 'unknown');
      showAlert(error.response?.data?.message || 'Failed to save content', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndContinue = async () => {
    if (!bookId) { showAlert('No book selected. Please start from the book creation flow.', 'warning'); return; }

    setIsSaving(true);
    try {
      const processedSections = await processAndUploadImages(sections);
      setSections(processedSections);

      await axios.put(endpoints.updateBook.replace(':id', bookId), buildSavePayload(processedSections));
      hasUnsavedChanges.current = false;
      // The editor only ever handles editor-content books (uploaded-file books never
      // reach /notion) — always route through the Preview screen, since that's where
      // Preview now lives (removed from the toolbar). Pre-existing books can still
      // preview there, they just aren't blocked from continuing without approving.
      await axios.put(endpoints.updateBookStep.replace(':id', bookId), { currentStep: 'book-preview' });
      navigate(`/books?id=${bookId}`);
    } catch (error: any) {
      const errMsg = error.response?.data?.message || 'Failed to save content';
      trackSaveAndContinueFailed(errMsg);
      showAlert(errMsg, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const addChapter = () => {
    const chapterCount = sections.filter(s => s.type === 'chapter').length;
    const newChapter: Section = { id: `chapter-${chapterCount + 1}`, title: `Chapter ${chapterCount + 1}`, content: '', type: 'chapter' };
    const lastChapterIndex = sections.map(s => s.type).lastIndexOf('chapter');
    const newSections = [...sections];
    newSections.splice(lastChapterIndex + 1, 0, newChapter);
    setSections(newSections);
    trackNewPage('chapter', newChapter.title);
  };

  const addFrontMatter = (id: string, title: string) => {
    const newSection: Section = { id, title, content: '', type: 'front-matter' };
    const newSections = [...sections];
    // Insert in the default order among existing front matter
    const defaultIndex = DEFAULT_FRONT_MATTER.findIndex(fm => fm.id === id);
    const frontMatterSections = newSections.filter(s => s.type === 'front-matter');
    // Find the correct position: after the last front-matter section whose default order is before this one
    let insertIndex = 0;
    for (const fm of frontMatterSections) {
      const fmDefaultIndex = DEFAULT_FRONT_MATTER.findIndex(d => d.id === fm.id);
      const fmActualIndex = newSections.indexOf(fm);
      if (fmDefaultIndex < defaultIndex) {
        insertIndex = fmActualIndex + 1;
      }
    }
    // If no front-matter before this one, insert at position 0
    newSections.splice(insertIndex, 0, newSection);
    setSections(newSections);
    setActiveSectionId(id);
    trackNewPage('front-matter', title);
  };

  const addEndMatter = () => {
    const endMatterCount = sections.filter(s => s.type === 'end-matter').length;
    const newSection: Section = {
      id: `end-matter-${endMatterCount + 1}-${Date.now()}`,
      title: `End Matter ${endMatterCount + 1}`,
      content: '',
      type: 'end-matter',
    };
    const lastEndMatterIndex = sections.map(s => s.type).lastIndexOf('end-matter');
    const newSections = [...sections];
    if (lastEndMatterIndex >= 0) {
      newSections.splice(lastEndMatterIndex + 1, 0, newSection);
    } else {
      newSections.push(newSection);
    }
    setSections(newSections);
    setActiveSectionId(newSection.id);
    trackNewPage('end-matter', newSection.title);
  };

  const deleteSection = (id: string) => {
    const sectionToDelete = sections.find(s => s.id === id);
    if (window.confirm('Are you sure you want to delete this section?')) {
      trackDeletePage(sectionToDelete?.type || 'unknown', sectionToDelete?.title || '');
      setSections(sections.filter(s => s.id !== id));
      if (activeSectionId === id) setActiveSectionId(sections[0]?.id || '');
    }
  };

  const renameSection = (id: string, newTitle: string) => {
    setSections(sections.map(s => s.id === id ? { ...s, title: newTitle } : s));
  };

  const splitAtCursor = () => {
    if (!editor) return;

    const { from } = editor.state.selection;
    const docSize = editor.state.doc.content.size;

    // Nothing to split if cursor is at the end
    if (from >= docSize - 1) return;

    // Serialize content after cursor to HTML
    const afterSlice = editor.state.doc.slice(from, docSize);
    const serializer = DOMSerializer.fromSchema(editor.schema);
    const fragment = serializer.serializeFragment(afterSlice.content);
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);
    const newContent = wrapper.innerHTML;

    // Delete content after cursor from current section
    editor.chain().focus().deleteRange({ from, to: docSize }).run();

    // Create new chapter and insert after the current section
    const chapterCount = sections.filter(s => s.type === 'chapter').length;
    const newChapter: Section = {
      id: `chapter-${chapterCount + 1}-${Date.now()}`,
      title: `Chapter ${chapterCount + 1}`,
      content: newContent,
      type: 'chapter',
    };

    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    const newSections = [...sections];
    // Update current section content (editor already trimmed it, but sync state)
    newSections[currentIndex] = { ...newSections[currentIndex], content: editor.getHTML() };
    newSections.splice(currentIndex + 1, 0, newChapter);
    setSections(newSections);
    setActiveSectionId(newChapter.id);
    trackNewPage('chapter', newChapter.title);
  };

  if (!editor) return <div>Loading editor...</div>;

  const handleTourComplete = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    setShowTour(false);
  };

  return (
    <div className="notion-editor">
      {isLoading && <div className="loading-overlay"><div className="loading-spinner">Loading book content...</div></div>}

      {!bookId && (
        <div className="no-book-warning">
          No book selected. Content will not be saved.
          <button onClick={() => navigate('/books')}>Go to Book Creation</button>
        </div>
      )}

      <EditorToolbar
        editor={editor}
        onImageUpload={handleImageUpload}
        onSave={handleSaveContent}
        onSaveAndContinue={handleSaveAndContinue}
        onGoBack={() => navigate(-1)}
        onSplitAtCursor={splitAtCursor}
        isSaving={isSaving}
        hasBookId={!!bookId}
        grammarErrorCount={errorCount}
        isCheckingGrammar={isChecking}
        onTriggerGrammarCheck={triggerCheck}
      />

      <div className="notion-body">
        <SectionSidebar
          bookTitle={bookTitle}
          sections={sections}
          activeSectionId={activeSectionId}
          onSectionSelect={setActiveSectionId}
          onSectionDelete={deleteSection}
          onSectionRename={renameSection}
          onAddChapter={addChapter}
          onAddFrontMatter={addFrontMatter}
          onAddEndMatter={addEndMatter}
          onDragEnd={handleDragEnd}
        />

        <div className="notion-main">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />

          <div className="notion-editor-content" style={{ position: 'relative' }}>
            <input
              type="text"
              className="section-heading-input"
              value={sections.find(s => s.id === activeSectionId)?.title || ''}
              onChange={(e) => renameSection(activeSectionId, e.target.value)}
              placeholder="Untitled Section"
            />
            <EditorContent editor={editor} />
            {activeError && (
              <GrammarSuggestionPopup
                editor={editor}
                error={activeError}
                onApplySuggestion={applySuggestion}
                onDismiss={dismissError}
                onClose={() => setActiveError(null)}
              />
            )}
          </div>
        </div>
      </div>

      {showTour && !isLoading && <EditorTour onComplete={handleTourComplete} />}
      <TipsPanel />
    </div>
  );
};

export default NotionEditor;
